<template>
  <div class="root-container">
    <BIcon
      class="back-icon"
      outlined
      size="large"
      icon-weight="light"
      :disabled="!canGoBack"
      @click="backToLead"
    >
      arrow_circle_left
    </BIcon>
    <div class="lead-info">
      <div class="company-name">
        {{ callTarget?.companyInfo?.companyName }}
      </div>
      <div
        v-if="callTarget?.callTargetInfo?.name"
        class="call-target-name"
      >
        {{ callTarget?.callTargetInfo?.name }}
      </div>
    </div>
    <div class="call-info">
      <BIcon
        type="gray"
      >
        call
      </BIcon>
      <div>
        {{ callResultTime }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, defineEmits } from 'vue';
import { CallResult, CallTarget } from '@/api/openapi';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import { useWait } from '@/composable/vue-wait';
import { formatShorterDate, formatShortTime } from '@/utils/date-time';

type TProps = {
  callResult: CallResult | null;
  backToLead: () => void;
  canGoBack: boolean;
}
type TEmits = {
  'loading': [boolean];
}

const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

const { doActionWithWait, wait } = useWait();

const callTarget = ref<CallTarget>();
const callTargetId = computed(() => props.callResult?.callTargetId);

const callResultTime = computed(() => {
  const startedAtDate = formatShorterDate(props.callResult?.startedAt);
  const startedAtTime = formatShortTime(props.callResult?.startedAt);
  const endedAtTime = formatShortTime(props.callResult?.endedAt);
  return `${startedAtDate} ${startedAtTime} - ${endedAtTime}`;
});

const loading = computed(() => {
  return wait.is('fetchCallTargetWait');
});

const fetchCallTarget = async () => {
  doActionWithWait('fetchCallTargetWait', async () => {
    const api = new CallTargetApiService();
    if (!callTargetId.value) return;
    const { data: { callTarget: callTargetRes } } = await api.getCallTarget({ request: { callTargetId: callTargetId.value } });
    callTarget.value = callTargetRes;
  });
};

watch(callTargetId, fetchCallTarget);
watch(loading, (newValue) => {
  emit('loading', newValue);
});
</script>

<style lang="scss" scoped>
.root-container {
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 20px 32px;
}

.lead-info {
  display: flex;
  gap: 13px;
}

.call-target-name {
  position: relative;
}

.call-target-name::before {
	position: absolute;
	top: calc(50% - 7px);
	left: -7px;
	content: "";
	width: 1px;
	height: 14px;
	background-color: $textcolor-base;
}


.call-info {
  display: flex;
  align-items: center;
  gap: 4px;

  color: $textcolor-light;
}

.back-icon {
  cursor: pointer;
}
</style>
